import React, { useMemo } from "react";
import DOMPurify from "dompurify";

/**
 * Iter 83 · Read-mode renderer for Current Reflection bodies.
 *
 * Handles BOTH storage shapes without a migration:
 *   · Legacy plain-text — pre-Iter 83 reflections were saved as raw
 *     strings. They render inside a single `<p>` with the historic
 *     Playfair drop-cap on the first character.
 *   · Rich-text HTML — Iter 83+ reflections come from TipTap and are
 *     server-sanitized by `bleach`. They render inside a
 *     `.prose-reflection` container so the app's editorial typography
 *     applies to every element (h1/h2/h3, p, ul, ol, blockquote,
 *     strong/em/u). A drop-cap is applied ONLY when the document
 *     starts with a normal paragraph — not when it starts with a
 *     heading or a list — so the drop-cap never fights structure.
 *
 * Detection heuristic:
 *   Treats the body as HTML iff it contains at least one recognised
 *   editorial tag (`<p`, `<h1|2|3`, `<ul`, `<ol`, `<blockquote`,
 *   `<strong`, `<em`, `<u`). This avoids false positives on legacy
 *   plain-text bodies that happen to contain a `<` character.
 */

const HTML_MARKER = /<(p|h1|h2|h3|ul|ol|blockquote|strong|em|u)(\s|>|\/)/i;

export const isRichReflection = (body) => {
  if (!body || typeof body !== "string") return false;
  return HTML_MARKER.test(body);
};

export default function RichReflectionRenderer({ body, columnMode = false, dropCap = true, testid }) {
  const rich = isRichReflection(body);
  const commonSurfaceStyle = {
    fontFamily: "'Merriweather', serif",
    fontSize: columnMode ? "1.0625rem" : "1rem",
    lineHeight: columnMode ? 1.7 : 1.6,
    maxWidth: columnMode ? "40rem" : "42rem",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    minWidth: 0,
  };

  // Defense in depth: reflection bodies are ALREADY server-sanitized
  // by `bleach` before persistence, but we ALSO run DOMPurify on the
  // client — belt-and-suspenders against a mis-configured writer or a
  // legacy record written before server sanitation was enforced. The
  // whitelist mirrors the editorial tag set (matching HTML_MARKER)
  // plus the inline tags TipTap emits (`br`, `a`, `code`), keeping
  // structure intact while stripping any `script` / `on*` handlers,
  // `javascript:` URLs, or `style` payloads that could escape.
  //
  // `useMemo` MUST run on every render (React rules-of-hooks), so
  // this precedes the `if (!rich)` early return. Cheap in the
  // non-rich case because `sanitize` bails on empty/plain input.
  const safeHtml = useMemo(
    () =>
      DOMPurify.sanitize(rich ? (body || "") : "", {
        ALLOWED_TAGS: [
          "p", "h1", "h2", "h3", "ul", "ol", "li",
          "strong", "em", "u", "blockquote", "br", "a", "code",
        ],
        ALLOWED_ATTR: ["href", "target", "rel"],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      }),
    [body, rich]
  );

  if (!rich) {
    return (
      <p
        className="leading-relaxed text-foreground prose-reflection prose-reflection--legacy"
        style={commonSurfaceStyle}
        data-testid={testid}
      >
        {dropCap && (body || "").length > 0 && (
          <span
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: columnMode ? "3.5rem" : "2.5rem",
              fontWeight: 900,
              float: "left",
              lineHeight: 0.85,
              marginRight: columnMode ? "0.65rem" : "0.5rem",
              marginTop: "0.35rem",
            }}
            className="text-primary"
          >
            {body.charAt(0)}
          </span>
        )}
        {(body || "").slice(dropCap ? 1 : 0)}
      </p>
    );
  }

  // Rich HTML. The `prose-reflection` class carries the editorial
  // typography rules (see index.css). Drop-cap is applied only when
  // the very first block is a plain paragraph — done in CSS via a
  // `:first-child` selector, so we do nothing here beyond flagging
  // that the drop-cap should apply. `safeHtml` is DOMPurify-sanitized
  // (see top of function).
  const html = { __html: safeHtml };
  return (
    <div
      className={`prose-reflection text-foreground ${dropCap ? "prose-reflection--dropcap" : ""}`}
      style={commonSurfaceStyle}
      data-testid={testid}
      dangerouslySetInnerHTML={html}
    />
  );
}
